import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BrainCircuit, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import CourseCard from "@/components/CourseCard";
import EnquiryDialog from "@/components/EnquiryDialog";
import { Button } from "@/components/ui/button";

const TRACKS = [
  { title: "AI & Machine Learning", items: ["AI Foundations", "Machine Learning", "Applied AI", "Deep Learning"] },
  { title: "Generative AI", items: ["Generative AI", "Prompt Engineering", "AI Productivity", "AI Content Creation", "AI Automation", "AI Agents"] },
  { title: "Data", items: ["Data Analytics", "Data Science", "Python for Data Science", "Business Intelligence"] },
  { title: "AI Career Tracks", items: ["AI Data Scientist", "AI Developer", "Generative AI Specialist", "AI Automation Specialist", "Data Analyst", "ML Practitioner"] },
];

export default function CareerPrograms() {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses", { params: { category: "ai-accelerator", limit: 20 } })
      .then((r) => setCourses(r.data.items)).catch(() => {});
  }, []);

  return (
    <div className="bg-navy-base min-h-screen" data-testid="career-programs-page">
      <SEO title="AI Accelerator Career Programs in Jorhat | XTM TECHNOLOGIES"
        description="Accelerate your career with Artificial Intelligence, Generative AI, Data, Automation and emerging technologies at XTM FutureSkills Academy, Jorhat." />
      <section className="relative hero-glow overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-3">Flagship Track</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-4" data-testid="career-programs-heading">
            AI Accelerator <span className="text-gradient-orange">Career Programs</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl leading-relaxed mb-8">
            Accelerate your career with Artificial Intelligence, Generative AI, Data, Automation and emerging technologies.
          </p>
          <div className="flex flex-wrap gap-4">
            <EnquiryDialog type="counselling" title="AI Career Counselling" trigger={
              <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-full px-8 transition-colors" data-testid="ai-counselling-btn">Talk to a Counsellor</Button>
            } />
            <Button asChild size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full px-8 transition-colors" data-testid="ai-ask-ai-btn">
              <Link to="/career-ai">Ask XTM Career AI <BrainCircuit className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {TRACKS.map((t) => (
            <div key={t.title} className="bg-card border border-white/10 rounded-2xl p-6 hover:border-orange-500/40 transition-colors" data-testid={`track-${t.title.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
              <h3 className="font-display font-semibold text-white mb-4">{t.title}</h3>
              <ul className="space-y-2">
                {t.items.map((i) => (
                  <li key={i} className="text-sm text-slate-400 flex gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />{i}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-8" data-testid="ai-courses-heading">Programs Open for Admission</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ai-courses-grid">
          {courses.map((c) => <CourseCard key={c.slug} course={c} />)}
        </div>
        <div className="mt-12 text-center">
          <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full" data-testid="ai-view-pathway-btn">
            <Link to="/career-pathways">View the Class 12 → AI Career Roadmap <ArrowRight className="w-4 h-4 ml-2" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
